import React from 'react';
import PropTypes from 'prop-types';
import { Box } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import { ChartWrapper } from 'src/components';
import { eeccCategoriesMapper } from 'src/utils/eeccCategoriesMapper';


const useStyles = makeStyles(() => ({
  chartContainer: {
    height: '400px',
    position: 'relative',
  },
}));

// Utilidades para obtener color y label desde eeccCategoriesMapper
const getConservationColor = cat => eeccCategoriesMapper[cat]?.backgroundColor || '#666';
const getConservationLabel = cat => eeccCategoriesMapper[cat]?.text || cat;

const SpeciesTrendsChart = ({ speciesData = [] }) => {
  const classes = useStyles();

  // Procesar datos para Chart.js
  const getChartData = React.useMemo(() => {
    if (!speciesData.length) {
      return {
        labels: [],
        datasets: [],
      };
    }

    // Usar años fijos de 2020 a 2024
    const years = [ 2020, 2021, 2022, 2023, 2024 ];

    // Agrupar especies por estado de conservación (categories)
    const speciesByStatus = speciesData.reduce((acc, species) => {
      const status = species.categories || 'Sin categoría';
      if (!acc[status]) {
        acc[status] = [];
      }
      acc[status].push(species);
      return acc;
    }, {});

    // Obtener los estados de conservación presentes en los datos
    const conservationStatuses = Object.keys(speciesByStatus).sort();

    // Crear datasets para cada estado de conservación
    const datasets = conservationStatuses.map(status => {
      const data = years.map(year =>
        speciesByStatus[status].reduce((sum, species) =>
          sum + (species.occurrences?.[year] || 0), 0));

      return {
        label: `${status} - ${getConservationLabel(status)}`,
        data,
        borderColor: getConservationColor(status),
        backgroundColor: getConservationColor(status),
        fill: false,
        tension: 0.1,
      };
    });

    return {
      labels: years,
      datasets,
    };
  }, [ speciesData ]);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const label = context.dataset.label || '';
            const value = Math.round(context.parsed.y).toLocaleString('es-CL', {
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            });
            return `${label}: ${value} individuos`;
          },
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: 'Abundancia',
        },
        ticks: {
          callback: function(value) {
            return Math.round(value).toLocaleString('es-CL', {
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            });
          },
        },
      },
      x: {
        title: {
          display: true,
          text: 'Año',
        },
      },
    },
    interaction: {
      intersect: false,
      mode: 'index',
    },
    elements: {
      point: {
        radius: 4,
        hoverRadius: 6,
      },
    },
  };

  return (
    <Box className={classes.chartContainer}>
      <ChartWrapper
        type="line"
        data={getChartData}
        options={chartOptions}
      />
    </Box>
  );
};

SpeciesTrendsChart.propTypes = {
  speciesData: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number,
    scientificName: PropTypes.string,
    kingdom: PropTypes.string,
    origin: PropTypes.string,
    categories: PropTypes.string,
    occurrences: PropTypes.object,
  })),
};


export {
  SpeciesTrendsChart,
};
